import React from "react";
import { Sparkles, Loader2, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { startCheckout } from "../lib/api";
import {
  trackAddToCart,
  trackInitiateCheckout,
} from "../lib/marketingPixels";
import { toast } from "sonner";
import MiniFeedbackToast from "./MiniFeedbackToast";
import { track } from "../lib/analytics";
import { trackFP } from "../lib/firstPartyAnalytics";
import { useReaderAppMode } from "../lib/readerAppMode";
import { useAuth } from "../context/AuthContext";

/**
 * Modal shown when a Free user hits the tutorial paywall (HTTP 402,
 * tutorial #4 onwards) OR taps a Pro-locked surface (library tile,
 * "Request a tutorial," etc.). Inline-rendered (no portal) — caller
 * controls visibility.
 *
 * Bounce intercept: if a user closes this modal without starting
 * checkout, we replace it with a one-line "what was missing?" toast —
 * paywall bounces are the highest-leverage feedback to unlock
 * pricing-tier insights.
 */
const UpgradeModal = ({ open, onClose, used = 3, limit = 3 }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [bounceToast, setBounceToast] = useState(false);
  const [startedCheckout, setStartedCheckout] = useState(false);
  const readerApp = useReaderAppMode();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Guests can't subscribe — no email, no way back in. Route them
  // through /login first so the paying customer is reachable.
  const isGuest = !!(
    user && (user.email || "").toLowerCase().endsWith("@guest.trubeaute.local")
  );

  // When the modal opens, fire the paywall_shown event so it lands in the
  // funnel even when the user reaches it directly (vs through Analyzing).
  useEffect(() => {
    if (open) {
      setStartedCheckout(false);            // reset for this open
      setBounceToast(false);
    }
  }, [open]);

  const upgrade = async () => {
    if (loading) return;
    // TikTok/Taboola/GTM: AddToCart fires immediately on Subscribe
    // click — before guest-redirect / async / checkout failures so
    // it always lands.
    trackAddToCart({
      content_id: "pro_annual",
      content_name: "Pro · Annual",
      content_type: "subscription",
      value: 49.99,
      currency: "USD",
      quantity: 1,
    });
    if (isGuest) {
      toast.info(t("upgradeModal.guestUpsellToast"));
      onClose?.();
      navigate("/login", { state: { next: "/pricing", reason: "subscribe" } });
      return;
    }
    setLoading(true);
    try {
      // Default-select annual on the paywall (better LTV, matches /pricing).
      track("checkout_started", { plan: "pro_annual", used, limit });
      trackFP("checkout_started", { plan: "pro_annual" });
      setStartedCheckout(true);
      const { url } = await startCheckout("pro_annual");
      trackInitiateCheckout({
        content_id: "pro_annual",
        value: 49.99,
        currency: "USD",
      });
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("upgradeModal.errorCheckout"));
      setLoading(false);
    }
  };

  // The user dismissed the modal. If they haven't kicked off checkout,
  // show the bounce-intercept toast on top before fully closing.
  const handleDismiss = () => {
    if (!startedCheckout && !bounceToast) {
      setBounceToast(true);
      return;
    }
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#2C2123]/60 backdrop-blur-sm px-4"
      data-testid="upgrade-modal"
    >
      <div className="relative w-full max-w-md bg-white rounded-3xl p-8 soft-shadow-lg">
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-[#8A7D82] hover:text-[#2C2123]"
          data-testid="upgrade-close-btn"
          aria-label={t("upgradeModal.closeAria")}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="h-12 w-12 rounded-full bg-[#F2E8E9] flex items-center justify-center mb-5">
          <Sparkles className="h-6 w-6 text-[#9B6A6C]" />
        </div>
        {readerApp ? (
          // Reader-app build: NO price, NO "upgrade", NO external-pay
          // CTA. Apple Review reads this tree — everything here must
          // be compliant with Guideline 3.1.3(a).
          <>
            <h2
              className="text-3xl font-light text-[#2C2123] mb-3 leading-tight"
              data-testid="upgrade-modal-readerapp-heading"
            >
              {t("upgradeModal.readerappHeading")}
            </h2>
            <p className="text-[#5C4B51] mb-7">
              {t("upgradeModal.readerappBody")}
            </p>
            <Link
              to="/login"
              className="w-full rounded-full bg-[#9B6A6C] text-white py-4 hover:bg-[#805557] transition flex items-center justify-center gap-2 shadow-[0_6px_20px_rgba(155,106,108,0.35)] mb-3"
              data-testid="upgrade-readerapp-signin"
              onClick={onClose}
            >
              <Sparkles className="h-4 w-4" />
              {t("upgradeModal.readerappSignIn")}
            </Link>
          </>
        ) : (
          <>
            <h2 className="text-3xl font-light text-[#2C2123] mb-3 leading-tight">
              {t("upgradeModal.headlineBefore")}{" "}
              <span className="font-serif italic text-[#5D4050]">
                {t("upgradeModal.headlineEmphasis")}
              </span>{" "}
              {t("upgradeModal.headlineAfter")}
            </h2>
            <p className="text-[#5C4B51] mb-6">
              {t("upgradeModal.body")}
            </p>

            <div className="space-y-2 mb-7 text-sm">
              <Row label={t("upgradeModal.row1")} />
              <Row label={t("upgradeModal.row2")} />
              <Row label={t("upgradeModal.row3")} />
              <Row label={t("upgradeModal.row4")} />
            </div>

            <button
              onClick={upgrade}
              disabled={loading}
              className="w-full rounded-full bg-[#9B6A6C] text-white py-4 hover:bg-[#805557] transition flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_6px_20px_rgba(155,106,108,0.35)] mb-3"
              data-testid="upgrade-pro-cta"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {t("upgradeModal.ctaAnnual")}
            </button>
            <button
              onClick={() => {
                if (loading) return;
                // Same AddToCart → InitiateCheckout pair as the annual
                // path, but with the monthly plan ($5.99).
                trackAddToCart({
                  content_id: "pro_monthly",
                  content_name: "Pro · Monthly",
                  content_type: "subscription",
                  value: 5.99,
                  currency: "USD",
                  quantity: 1,
                });
                if (isGuest) {
                  toast.info(t("upgradeModal.guestUpsellToast"));
                  onClose?.();
                  navigate("/login", {
                    state: { next: "/pricing", reason: "subscribe" },
                  });
                  return;
                }
                setLoading(true);
                track("checkout_started", {
                  plan: "pro_monthly",
                  used,
                  limit,
                });
                trackFP("checkout_started", { plan: "pro_monthly" });
                setStartedCheckout(true);
                startCheckout("pro_monthly")
                  .then(({ url }) => {
                    trackInitiateCheckout({
                      content_id: "pro_monthly",
                      value: 5.99,
                      currency: "USD",
                    });
                    window.location.href = url;
                  })
                  .catch((e) => {
                    toast.error(
                      e?.response?.data?.detail || t("upgradeModal.errorCheckout"),
                    );
                    setLoading(false);
                  });
              }}
              disabled={loading}
              className="w-full text-center text-sm text-[#5C4B51] hover:text-[#9B6A6C] mb-2 disabled:opacity-60"
              data-testid="upgrade-monthly-cta"
            >
              {t("upgradeModal.ctaMonthly")}
            </button>
            <Link
              to="/pricing"
              className="block w-full text-center text-sm text-[#9B6A6C] hover:underline"
              data-testid="upgrade-see-plans-link"
            >
              {t("upgradeModal.ctaComparePlans")}
            </Link>
          </>
        )}
      </div>

      {/* Bounce intercept — appears over the modal when the user tries to
          close without starting checkout. Closing it (submit / dismiss /
          auto-dismiss) finishes the close. */}
      <AnimatePresence>
        {bounceToast && (
          <MiniFeedbackToast
            context="paywall_bounced"
            onClose={() => {
              setBounceToast(false);
              onClose?.();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const Row = ({ label }) => (
  <div className="flex items-center gap-2 text-[#3C2F33]">
    <span className="h-1.5 w-1.5 rounded-full bg-[#9B6A6C]" />
    {label}
  </div>
);

export default UpgradeModal;
